import { useCallback, useState } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';

import { logger } from '@zep/core-logger';

import { useGetQuizSetByEnterCode, ZepQuizApiError } from '@/api';
import { ClassroomRoleResRole } from '@/api/gen/_model';
import { getBoardByEnterCode } from '@/api/gen/board-controller/board-controller.gen';
import { getClassroomRole } from '@/api/gen/classroom/classroom.gen';
import { verifyClassCode } from '@/api/gen/classroom-student/classroom-student.gen';
import { resolveCode } from '@/api/gen/enter-code/enter-code.gen';
import { useViewport } from '@/hooks';
import { useUser } from '@/lib/auth';
import { CLASSROOM_ROUTES } from '@/shared/constants/routes';
import { useJoinClassroomErrorMessages } from '@/shared/features/classroom/hooks';
import { trackClassroomEvent } from '@/shared/features/classroom/utils';

import { openLoginRequiredDialog } from '../components';

export function useJoinFlow() {
  const router = useRouter();
  const { t } = useTranslation(['common']);
  const { resolve: resolveClassroomError } = useJoinClassroomErrorMessages();
  const { isMobile } = useViewport();
  const { mutateAsync: getQuizSetByEnterCode } = useGetQuizSetByEnterCode();
  const { isAuthenticated, isInitialized } = useUser();
  const [errorMessage, setErrorMessage] = useState<string>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const redirectToLogin = useCallback(
    (callbackUrl: string) => {
      void router.push(`/login?callbackUrl=${encodeURIComponent(callbackUrl)}`);
    },
    [router],
  );

  const startJoinFlow = useCallback(
    async ({ code }: { code: string }) => {
      const trimmed = code.trim().toUpperCase();
      setErrorMessage(undefined);

      if (!trimmed) {
        setErrorMessage(t('common:classroomJoin.error.emptyCode'));
        return;
      }

      // U-prefix = 유저(학생 진입) 코드. /join 이 아니라 /code-login 이 처리한다.
      // 코드 존재 확인 없이(oracle 방지) 쿼리파람만 들고 보낸다 → code-login 의 ?code= 자동검증이 이어받음.
      if (trimmed.startsWith('U')) {
        void router.push(`/code-login?code=${encodeURIComponent(trimmed)}`);
        return;
      }

      setIsSubmitting(true);
      // 클래스 코드 검증 단계에 진입했는지 추적 — catch 에서 fail_verify_classroom_code
      // 를 발화할지 판단한다(분류가 BE 로 이전돼 catch 시점엔 type 을 알 수 없으므로).
      let isClassroomVerification = false;
      try {
        // 코드 종류 분류는 백엔드 단일 소스(resolveCode). FE 는 type 으로 라우팅만.
        const { data: resolved } = await resolveCode({ code: trimmed });

        if (resolved.type === 'CLASSROOM' && resolved.classGroupId) {
          isClassroomVerification = true;
          trackClassroomEvent('submit_join_code', {
            source: 'join_page',
            codeType: 'classroom',
            codeLength: trimmed.length,
          });

          if (!isInitialized) {
            setErrorMessage(t('common:classroomJoin.error.authChecking'));
            return;
          }

          const { data: preview } = await verifyClassCode({
            classCode: resolved.classCode ?? trimmed,
          });
          trackClassroomEvent('success_verify_classroom_code', {
            source: 'join_page',
            classroomId: preview.classGroupId,
            isAuthenticated,
          });

          if (!isAuthenticated) {
            const callbackUrl = CLASSROOM_ROUTES.join(
              preview.classGroupId,
              resolved.classCode ?? trimmed,
            );
            // 확인 버튼 없이 안내 후 3초 뒤 자동 로그인 이동. dim/X 로 닫으면 취소.
            openLoginRequiredDialog(() => redirectToLogin(callbackUrl));
            return;
          }

          try {
            const { data: roleRes } = await getClassroomRole(
              preview.classGroupId,
            );
            if (
              roleRes.role === ClassroomRoleResRole.OWNER ||
              roleRes.role === ClassroomRoleResRole.MEMBER
            ) {
              void router.push(
                CLASSROOM_ROUTES.detail(
                  preview.classGroupId,
                  roleRes.role === ClassroomRoleResRole.OWNER,
                  { isMobile },
                ),
              );
              return;
            }
          } catch {
            if (preview.isMember) {
              void router.push(CLASSROOM_ROUTES.detail(preview.classGroupId));
              return;
            }
          }

          void router.push(
            CLASSROOM_ROUTES.join(
              preview.classGroupId,
              resolved.classCode ?? trimmed,
            ),
          );
          return;
        }

        if (resolved.type === 'BOARD' && resolved.enterCode != null) {
          const { data: boardDetail } = await getBoardByEnterCode(
            resolved.enterCode,
          );
          logger.track('code_complete');
          void router.push(`/board/${boardDetail.board.id}`);
          return;
        }

        if (resolved.type === 'QUIZ' && resolved.enterCode != null) {
          const { data } = await getQuizSetByEnterCode({
            data: { enterCode: resolved.enterCode },
          });
          logger.track('code_complete');
          logger.track('enter_play', {
            location: 'enter_code',
            spaceId: data.spaceHashId,
          });
          void router.push(`/play/${data.entryMapHashId}`);
          return;
        }

        setErrorMessage(t('common:enterCode.input.inputAgain'));
      } catch (error) {
        console.error(error);
        // 클래스 코드 검증(verifyClassCode 등) 중 실패한 경우만 발화.
        // resolveCode 자체 실패(타입 미확정)는 클래스 실패로 단정할 수 없어 제외한다.
        if (isClassroomVerification) {
          trackClassroomEvent('fail_verify_classroom_code', {
            source: 'join_page',
            errorStatus:
              error instanceof ZepQuizApiError ? error.status : undefined,
          });
        }
        // 서버 status 를 로컬라이즈 메시지로 노출하고, 미매핑이면 일반 안내로 폴백한다.
        setErrorMessage(
          resolveClassroomError(
            error,
            t('common:classroomJoin.error.invalidCode'),
          ),
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      getQuizSetByEnterCode,
      isAuthenticated,
      isInitialized,
      isMobile,
      redirectToLogin,
      resolveClassroomError,
      router,
      t,
    ],
  );

  return {
    errorMessage,
    isAuthInitialized: isInitialized,
    isSubmitting,
    startJoinFlow,
  };
}
